import React, { useEffect, useRef, useCallback } from 'react';
import { LegacyColumn as Column } from 'components/MaterialTable/MatDataGridTypes';
import { useAuth } from '../../lib/Auth';
import { CampaignListFilter } from './CampaignListFilter';
import { getAllCampaigns } from '../../requests/campaigns';
import styled, { createGlobalStyle } from 'styled-components';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { getItems } from 'lib/SessionStorage';
import { CampaignContext } from 'context/campaign';
import { renderToast } from 'components/UI/ToastNotification';
import { GENERAL_CONSTANTS, STORAGE_KEYS } from 'constants/general';
import MatDataGrid from 'components/MaterialTable/MatDataGrid';

const StyledButton = styled.button<any>`
  background: none;
  border: none;
  color: #FC780B;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  margin: 0 5px;
`;

const CampaignTableStyles = createGlobalStyle`
  .campaign-list-table-wrapper {
    /* Target Material React Table checkbox column selectors */
    .mrt-row-select,
    .mrt-table-head-cell-select,
    .mrt-table-body-cell-select,
    tbody tr td:first-child,
    thead tr th:first-child {
      padding: 8px 12px !important;
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
    }

    .mrt-row-select:has(input[type="checkbox"]),  
    .mrt-table-head-cell-select,  
    .mrt-table-body-cell-select,  
    table thead tr th:first-child, 
    table tbody tr td:first-child,  
    .MuiTableHead-root .MuiTableCell-head:first-child,  
    .MuiTableBody-root .MuiTableCell-body:first-child,  
    th[data-column-id="mrt-row-select"],  
    td[data-column-id="mrt-row-select"] {
      width: 2% !important;
      min-width: 2% !important;
      max-width: 2% !important;
      padding: 4px !important;
    }
    
    /* Target Material-UI checkbox cells */
    .MuiTableCell-paddingCheckbox {
      padding: 8px 12px !important;
    }
    
    /* Target the actual checkbox component */
    .MuiCheckbox-root {
      padding: 4px !important;
      margin: 0 !important;
    }
    
    /* Additional fallback selectors */
    table tbody tr td:first-child,
    table thead tr th:first-child {
      padding: 8px 12px !important;
    }
    
    /* Material React Table specific class targeting */
    [class*="MuiTableCell"]:first-child {
      padding: 8px 12px !important;
    }
    
    /* Ensure all other data columns have consistent padding */
    table thead tr th:not(:first-child),
    table tbody tr td:not(:first-child) {
      padding: 14px 8px !important;
    }
    
    /* Vendor column width - target second column when checkboxes are enabled */
    table thead tr th:nth-child(2),
    table tbody tr td:nth-child(2) {
      width: 150px !important;
      min-width: 150px !important;
      padding: 14px 8px !important;
    }
  }
`;


interface Props {
  selectedVendorId?: number;
  filters: CampaignListFilter;
  searchTerm: string;
  campaignColumns: Column<any>[];
  setShowDeleteModal?: Function;
  setShowPublishModal?: Function;
  setSelectedRows?: Function;
  selectedRows: any[];
  reload?: boolean;
  setReload?: Function;
  setLoading?: Function;
  handleEdit?: Function;
  handleDelete?: Function;
  loading: boolean;
}

const CampaignListTable = ({ selectedVendorId,
  filters,
  searchTerm,
  campaignColumns,
  setSelectedRows,
  reload,
  setReload,
  setLoading,
  handleEdit,
  handleDelete,
  loading
}: Props) => {
  const { updateStats, setStatsLoading } = React.useContext(CampaignContext);
  const { token } = useAuth();
  const [columns, setColumns] = React.useState(campaignColumns);
  const [tablesData, setTablesData] = React.useState<any>({
    data: [],
    page: 0,
    totalCount: 0,
  });
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(0);
  const [sortField, setSortField] = React.useState<string | undefined>();
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | undefined>();

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Setup columns with action handlers
  useEffect(() => {
    const actionColumn = {
      render: (rowData: any) => {
        return <FlexboxContainerComponent>
          <StyledButton onClick={() => handleEdit?.(rowData?.id)}>{'Edit'}</StyledButton>
          <StyledButton onClick={() => handleDelete?.(rowData?.id)}>{'Remove'}</StyledButton>
        </FlexboxContainerComponent>
      }
    };

    const updatedColumns = campaignColumns.map((col) => {
      if (col.field === 'actions') {
        return { ...col, ...actionColumn };
      }
      // Apply defaultSort to the currently sorted column
      if (col.field === sortField && sortOrder) {
        return { ...col, defaultSort: sortOrder };
      }
      return { ...col, defaultSort: undefined };
    });
    setColumns(updatedColumns);
  }, [campaignColumns, handleEdit, handleDelete, sortField, sortOrder]);

  // Build query params
  const buildQueryParams = useCallback(() => {
    const countries = getItems(STORAGE_KEYS.COUNTRIES);
    let params = `page=${page + 1}&pageSize=${rowsPerPage}`;

    if (filters) {
      if (filters.campaignTypes && filters.campaignTypes.length > 0) {
        params += `&campaignTypeIds=${filters.campaignTypes.map((ct) => ct.id).join(',')}`;
      }
      if (filters.launchCompanies && filters.launchCompanies.length > 0) {
        params += `&launchCompanyIds=${filters.launchCompanies.map((lc) => lc.id).join(',')}`;
      }
      if (filters.vendor && filters.vendor.length > 0 && !selectedVendorId) {
        params += `&vendorIds=${filters.vendor.map((v) => v.id).join(',')}`;
      }
      if (filters.country.id) {
        const countryId = countries.find((c: any) => c.countryIso === filters.country.id)?.id;
        params += `&countryId=${countryId}`;
      }
      if (filters.startDate) {
        params += `&startDate=${new Date(filters.startDate).toISOString()}`;
      }
      if (filters.endDate) {
        params += `&endDate=${new Date(filters.endDate).toISOString()}`;
      }
    }

    if (selectedVendorId) {
      params += `&vendorIds=${selectedVendorId}`;
    }

    if (searchTerm && searchTerm.trim() !== '') {
      params += `&search=${searchTerm}`;
    }

    if (sortField && sortOrder) {
      params += `&sortField=${sortField}&sortOrder=${sortOrder}`;
    }

    return params;
  }, [page, rowsPerPage, filters, selectedVendorId, searchTerm, sortField, sortOrder]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading?.(true);
      setStatsLoading?.(true);
      const queryParams = buildQueryParams();
      const result = await getAllCampaigns?.(queryParams, token);

      if (isMounted.current) {
        setTablesData({
          data: result.data,
          page: result.meta.currentPage - 1,
          totalCount: result.meta.totalRecords,
        });
        updateStats?.(result.statistics);
        setReload?.(false);
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error('API error:', error);
        renderToast('error', error.message || `${GENERAL_CONSTANTS.FAILED_TO_FETCH} ${GENERAL_CONSTANTS.campaigns}`);
      }
    } finally {
      setLoading?.(false);
      setStatsLoading?.(false);
    }
  }, [buildQueryParams, token, setLoading, setStatsLoading, updateStats, setReload]);

  // Fetch data on dependency changes
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters, selectedVendorId, searchTerm, sortField, sortOrder, reload]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters, selectedVendorId, searchTerm]);

  const handleRowsPerPageChange = (newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1);
  };

  const handleSortChange = (orderBy: number, orderDirection: string) => {
    if (orderBy >= 0 && columns && columns[orderBy] && (orderDirection === 'asc' || orderDirection === 'desc')) {
      const field = String(columns[orderBy].field);
      setSortField(field);
      setSortOrder(orderDirection);
    }
  };

  const totalPages = Math.ceil(tablesData.totalCount / rowsPerPage);
  const printColumns = columns.filter(x => x.title !== 'Actions');

  return (
    <div className="campaign-list-table-wrapper">
      <CampaignTableStyles />
      <MatDataGrid
        columns={columns}
        data={tablesData.data}
        isLoading={loading}
        sorting
        enableSelection
        onSelectionChange={(rows) => setSelectedRows?.(rows)}
        onOrderChange={handleSortChange}
        headerStyle={{
          color: '#FFF',
          fontSize: '16px',
          height: 50,
          padding: '0px'
        }}
        rowStyle={{
          fontSize: '14px',
          wordWrap: 'break-word',
        }}
        customPagination={{
          itemsPerPage: rowsPerPage,
          setItemsPerPage: handleRowsPerPageChange,
          currentPage: page + 1,
          setCurrentPage: handlePageChange,
          totalPages: totalPages > 0 ? totalPages : 1,
          baseRoute: 'marketing',
          shouldRedirect: false
        }}
      />
      {/* Print table for marketing campaigns */}
      <div id="marketing-campaign-table" style={{ display: 'none' }}>
        <MatDataGrid
          columns={printColumns}
          data={tablesData.data}
          paging={false}
          sorting={false}
          headerStyle={{
            color: '#FFF',
            fontSize: '14px',
            height: 50,
            padding: '0px'
          }}
          rowStyle={{
            fontSize: '12px',
            wordWrap: 'break-word',
          }}
        />
      </div>
    </div>
  );
};

export default React.memo(CampaignListTable);
